import { yupResolver } from '@hookform/resolvers/yup';
import { mdiContentCopy } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Box, Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, useFieldArray, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import FuncionarioService from 'src/services/Funcionario.service';

import { Carteira as CarteiraModel } from '../../../models/Carteira.model';
import {
  enviarTransferenciaFormModelSchema,
  FuncionarioMin,
  Transferencias,
  VALOR_MAXIMO_TRANSFERENCIA
} from '../../../models/Transferencia.model';
import { CarteiraService } from '../../../services';
import { Colors } from '../../../shared/colors';
import CardCarteira from '../../components/CardCarteira';
import Loading from '../../components/Loading';

import ResumoTransferencia from './ResumoTransferencia';
import UsuarioTransferenciaCard from './UsuarioTransferenciaCard';

interface Props {
  funcionarios: FuncionarioMin[];
  onClose: () => void;
}

const FormEnviarTranferencia = ({ onClose, funcionarios }: Props) => {
  const form = useForm<Transferencias>({
    defaultValues: { funcionarios },
    resolver: yupResolver(enviarTransferenciaFormModelSchema)
  });

  const { fields, remove, update } = useFieldArray({
    control: form.control,
    name: 'funcionarios'
  });

  const [loading, setLoading] = useState(true);
  const [carteiras, setCarteiras] = useState<CarteiraModel[]>([]);
  const [valorTotal, setValorTotal] = useState(0);

  const transf = form.watch('funcionarios');

  const valorDistibuido = transf.reduce(
    (prev, cur) => prev + (isNaN(cur.valor) ? 0 : cur.valor),
    0
  );
  const valoresValidos =
    transf.length > 0 &&
    transf.every(
      ({ valor }) => Number.isFinite(valor) && valor > 0 && valor <= VALOR_MAXIMO_TRANSFERENCIA
    ) &&
    valorDistibuido <= VALOR_MAXIMO_TRANSFERENCIA;

  const [carteira] = carteiras;

  function replicarValor() {
    fields.forEach((data, i) => {
      update(i, { ...data, valor: valorTotal });
    });
  }

  const onValid = (data: Transferencias) => {
    setLoading(true);
    FuncionarioService.carregarCartao(data)
      .then(onClose)
      .finally(() => setLoading(false));
  };

  const onInvalid: SubmitErrorHandler<Transferencias> = (err) => {
    console.log(err);
  };

  useEffect(() => {
    setLoading(true);
    CarteiraService.getCarteiras()
      .then(setCarteiras)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading show={loading} />;

  return (
    <FormProvider {...form}>
      <Box width={1} flex={1} minHeight={0} display="flex" flexDirection="column">
        <Box flex={1} minHeight={0} overflow="auto" p={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {carteiras.map((carteira) => (
                <CardCarteira key={carteira.idCarteira} carteira={carteira} />
              ))}
            </Grid>

            <Grid item xs={12}>
              <Box
                p={1.5}
                borderRadius={1.5}
                sx={{
                  background: 'linear-gradient(110deg, #F4F9FF 0%, #EAF4FF 55%, #F8FBFF 100%)',
                  border: '1px solid #BFD9FA'
                }}
              >
                <Typography color="#123D81" fontSize={13} fontWeight={700} mb={1}>
                  Valor total a distribuir
                </Typography>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} sm={7.5}>
                    <NumericFormat
                      fullWidth
                      size="small"
                      decimalScale={2}
                      fixedDecimalScale
                      allowNegative={false}
                      isAllowed={({ floatValue }) =>
                        floatValue === undefined || floatValue <= VALOR_MAXIMO_TRANSFERENCIA
                      }
                      decimalSeparator=","
                      thousandSeparator="."
                      customInput={TextField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{
                              alignSelf: 'stretch',
                              backgroundColor: '#F5F6F8',
                              borderRight: '1px solid #DDE2E8',
                              height: 'auto',
                              maxHeight: 'none',
                              ml: -1.75,
                              mr: 1.5,
                              px: 1.25,
                              '& .MuiTypography-root': {
                                color: '#667085',
                                fontSize: 13,
                                fontWeight: 400
                              }
                            }}
                          >
                            R$
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#FFFFFF',
                          borderRadius: 1,
                          height: 40,
                          overflow: 'hidden',
                          '& fieldset': { borderColor: '#C8D0DA' },
                          '&:hover fieldset': { borderColor: '#C8D0DA' },
                          '&.Mui-focused fieldset': { borderColor: '#C8D0DA', borderWidth: 1 }
                        },
                        '& .MuiInputBase-input': {
                          color: '#667085',
                          fontSize: 14,
                          fontWeight: 400
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#98A2B3',
                          fontWeight: 400,
                          opacity: 1
                        }
                      }}
                      value={valorTotal ? valorTotal : ''}
                      placeholder="Digite o valor"
                      onValueChange={(values) => {
                        setValorTotal(values.floatValue ?? 0);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4.5}>
                    <Button
                      sx={{
                        borderColor: '#65A9F9',
                        color: '#0879E5',
                        fontSize: 12,
                        fontWeight: 600,
                        height: 40,
                        minWidth: 0,
                        textTransform: 'none',
                        whiteSpace: 'nowrap'
                      }}
                      fullWidth
                      variant="outlined"
                      startIcon={<Icon path={mdiContentCopy} size={0.65} />}
                      onClick={replicarValor}
                    >
                      Replicar valor
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} mb={1}>
              <Typography color={Colors.secondary} fontSize={14} fontWeight={600}>
                {fields.length === 1 ? 'Usuário selecionado' : 'Usuários selecionados'}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {fields.map((data, i) => (
              <UsuarioTransferenciaCard
                key={data.id}
                funcionario={data}
                index={i}
                tipo="envio"
                valor={transf[i]?.valor ?? 0}
                onRemove={() => remove(i)}
              />
            ))}
          </Grid>
        </Box>

        <Box
          p={2}
          display="flex"
          flexDirection="column"
          gap={2}
          bgcolor="white"
          sx={{ borderTop: '1px solid #D8E0EA', flexShrink: 0 }}
        >
          <ResumoTransferencia funcionarios={transf} tipo="envio" valorTotal={valorDistibuido} />
          {valorDistibuido > VALOR_MAXIMO_TRANSFERENCIA && (
            <Typography color="error" fontSize={12}>
              O total deve ser de até R$ 100.000,00.
            </Typography>
          )}
          <Box display="flex" gap={1.5} width={1}>
            <Button fullWidth variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={
                !valoresValidos ||
                !carteira ||
                valorDistibuido > carteira.saldo ||
                carteira.saldoBloqueado
              }
              onClick={form.handleSubmit(onValid, onInvalid)}
              sx={{ textTransform: 'none' }}
            >
              Confirmar envio
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default FormEnviarTranferencia;
